import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, TrendingUp, Zap, ShieldCheck } from "lucide-react";
import TradingChart from "@/components/TradingChart";
import { REGISTER_URL } from "@/data/plans";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const chartY = useTransform(scrollY, [0, 700], [0, 90]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" onMouseMove={onMove} className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#FFD700]/[0.05] blur-[140px]" />
        <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] rounded-full bg-[#2DACE2]/[0.05] blur-[140px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center relative z-10 w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#FFD700]/25 bg-[#FFD700]/5 px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <span className="live-dot w-2 h-2 rounded-full bg-[#22C55E]" />
            <span className="text-xs font-semibold tracking-[0.15em] uppercase text-[#FFD700]">
              5 Years Funding Traders Worldwide
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-[-0.06em] leading-[1.02] text-white"
          >
            <Line delay={0.25}>Trade Easy.</Line>
            <Line delay={0.4}>
              <span className="gold-text">No Bull.</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-6 text-base sm:text-lg text-[#999999] max-w-xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            Get funded up to <span className="text-white font-semibold">$400,000</span> with free prop
            trading mentorship, live market analysis, and institutional-grade execution powered
            by <span className="text-[#2DACE2] font-semibold">Match-Trader</span>. Zero personal risk.
            Payouts every 5–14 days.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-choose-challenge-button"
              className="btn-gold group inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-bold"
            >
              Choose Your Challenge
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#challenges"
              data-testid="hero-secondary-cta"
              onClick={(e) => {
                e.preventDefault();
                window.__lenis?.scrollTo("#challenges", { offset: -80 });
              }}
              className="btn-ghost inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white"
            >
              Compare Plans
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-8"
            data-testid="hero-stats"
          >
            {[
              ["$400K", "Max Funding"],
              ["80/20", "Profit Split"],
              ["5–14d", "Payout Cycle"],
            ].map(([v, l]) => (
              <div key={l}>
                <div className="text-2xl font-bold text-white tracking-[-0.03em]">{v}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#999999] mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: chartY }} className="relative [perspective:1200px]">
          <motion.div
            initial={{ opacity: 0, y: 60, rotateX: 12 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: rx, rotateY: ry }}
            className="glass-card rounded-2xl p-5 sm:p-6 relative"
            data-testid="hero-chart-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/25 flex items-center justify-center">
                  <TrendingUp size={16} className="text-[#FFD700]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">XAU/USD</div>
                  <div className="text-xs text-[#999999]">Match-Trader · Institutional Feed</div>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-full bg-[#22C55E]/10 border border-[#22C55E]/25 px-3 py-1">
                <span className="live-dot w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                <span className="text-xs font-semibold text-[#22C55E]">LIVE</span>
              </div>
            </div>
            <TradingChart />
            <div className="mt-4 grid grid-cols-3 gap-3">
              {[
                [Zap, "Execution", "< 30ms"],
                [ShieldCheck, "Uptime", "99.9%"],
                [TrendingUp, "Leverage", "Up to 1:100"],
              ].map(([Icon, l, v]) => (
                <div key={l} className="rounded-xl bg-[#030B16]/70 border border-white/5 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[#999999]">
                    <Icon size={12} />
                    <span className="text-[10px] uppercase tracking-[0.12em]">{l}</span>
                  </div>
                  <div className="text-sm font-semibold text-white mt-1">{v}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
